/**
 * FloatingSurface
 *
 * Reusable floating container that mirrors the chat input concept: a
 * rounded, translucent, bordered surface that lifts with a shadow.
 * Wrap anything (headers, toolbars, cards, inputs) to give it the
 * same floating look and optional docked positioning.
 *
 * Primary responsibilities:
 * - Provide the surface grammar (radius, border, blur, shadow, lift)
 * - Provide optional dock positioning (top/bottom, sticky/fixed)
 *
 * Dependencies:
 * - class-variance-authority
 */

'use client'

import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

// Surface look: matches the chat input container styling
const surfaceVariants = cva(
  'transition-[background-color,border-color,box-shadow] duration-200 ease-out',
  {
    variants: {
      // Visual treatment of the floating surface
      variant: {
        // Chat-input look: subtle slate/neutral translucent fill
        input:
          'border-input border bg-slate-50/50 shadow-sm focus-within:bg-slate-50/70 focus-within:shadow-md dark:bg-neutral-700/20 focus-within:dark:bg-neutral-800/20',
        // Frosted glass look for headers/toolbars over content
        glass:
          'border border-border/60 bg-background/70 shadow-sm backdrop-blur hover:shadow-md dark:border-white/10 dark:bg-neutral-900/60',
        // Solid raised card look
        solid: 'border border-border bg-background shadow-md hover:shadow-lg',
        // True floating: no fill, just border + lift so it reads as a
        // surface hovering over content (background shows through)
        floating:
          'border border-border/60 bg-transparent shadow-md hover:shadow-lg focus-within:shadow-lg dark:border-white/10'
      },
      // Corner radius grammar
      radius: {
        lg: 'rounded-lg',
        xl: 'rounded-xl',
        '2xl': 'rounded-2xl',
        // Pill-ish, matches the chat input (2rem)
        pill: 'rounded-[2rem]',
        full: 'rounded-full'
      },
      // Optional docking. Requires a positioned/relative parent for
      // sticky, or a viewport-relative context for fixed.
      dock: {
        none: '',
        'top-sticky': 'sticky top-3 z-30',
        'bottom-sticky': 'sticky bottom-3 z-30',
        'top-fixed': 'fixed left-1/2 top-3 z-30 -translate-x-1/2',
        'bottom-fixed': 'fixed bottom-3 left-1/2 z-30 -translate-x-1/2'
      }
    },
    defaultVariants: {
      variant: 'glass',
      radius: '2xl',
      dock: 'none'
    }
  }
)

export interface FloatingSurfaceProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof surfaceVariants> {
  // Render as a different element (e.g. 'header', 'nav', 'section')
  as?: React.ElementType
}

// Floating container wrapper
const FloatingSurface = React.forwardRef<HTMLDivElement, FloatingSurfaceProps>(
  ({ as, variant, radius, dock, className, ...props }, ref) => {
    const Comp = as ?? 'div'

    return (
      <Comp
        ref={ref}
        className={cn(surfaceVariants({ variant, radius, dock }), className)}
        {...props}
      />
    )
  }
)

FloatingSurface.displayName = 'FloatingSurface'

export { FloatingSurface, surfaceVariants }
